import type { IControl, Map, ControlPosition } from 'maplibre-gl';

// Interface for layer groups with nested support
interface LayerGroupConfig {
    id: string;
    heading: string;  // Renombrado (antes 'name')
    layers?: string[];  // Layer IDs that belong to this group
    layersTitles?: string[];  // Títulos personalizados para las capas
    layersVisible?: boolean[];  // Array de booleanos para estado inicial
    subgroups?: LayerGroupConfig[];  // Subgroups (nested)
    collapsed?: boolean;
}

// Extended options
type LayersControlOptions = {
  title?: string;
  customLabels?: Record<string, string>;
  legendServiceUrl?: string;
  opacityControl: boolean;
  layerGroups?: LayerGroupConfig[];
  ungroupedLayers?: string[];  // Capas sueltas sin grupo
  ungroupedLayersTitles?: string[];  // Títulos personalizados para capas sueltas
  ungroupedLayersVisible?: boolean[];  
  baseLayersHeading?: string;  
  ungroupedLayersHeading?: string;  
  onePointZoomLevel?: number;  // Option for zoom level when feature is only a point. Else, it depends on the view embracing all the features.
};

const defaultStyles = `
.layers-control {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.layers-control .panel {
  position: absolute;
  z-index: 1000;
  display: none;
  flex-direction: column;
  background: white;
  padding: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  width: max-content;
  max-width: 35vw;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  overflow-x: auto;
}

.maplibregl-popup-close-button {
  font-size: 18px;
  padding: 4px 8px;
}

.maplibregl-popup-content {
  max-width: 400px;
  min-width: 300px;
  width: auto;
}

@media (min-width: 768px) {
  .maplibregl-popup-content {
    max-width: 500px;
    min-width: 350px;
  }
}

.panel-title {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  cursor: pointer;
  margin: 0;
}

/* Tree structure styles */
.tree-group {
}

.group-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 0;
  font-weight: 600;
  background-color: #eee;
  margin: 4px 0;
  border-radius: 4px;
}

.group-header:hover {
  background-color: #e8e8e8;
}

.group-toggle {
  width: 20px;
  height: 20px;
  margin-right: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
}

.group-checkbox {
  margin-right: 8px;
  cursor: pointer;
}

.group-name {
  flex: 1;
  font-size: 1.1em;
}

.group-children {
  margin-left: 0.8em;
  padding-left: 13px;
  border-left: 1px dashed #afaeae;
}

.group-children.collapsed {
  display: none;
}

.layer-item {
  margin: 4px 0;
  padding: 2px 0;
  display: flex;
  align-items: center;
}

.layer-item label {
  margin-left: 4px;
  cursor: pointer;
  font-size: 1.1em;
  font-weight: normal;
}

.opacity-row {
  margin-left: 24px;
  margin-top: 2px;
  margin-bottom: 4px;
}

.opacity-row input[type="range"] {
  width: 80%;
  margin-left: 20px;
}

.legend-svg {
  margin-left: 24px;
  font-size: 12px;
}

.maplibregl-ctrl-top-left > .layers-control .panel,
.maplibregl-ctrl-top-right > .layers-control .panel {
  top: 100%;
}

.maplibregl-ctrl-bottom-left > .layers-control .panel,
.maplibregl-ctrl-bottom-right > .layers-control .panel {
  bottom: 100%;
}

.maplibregl-ctrl-top-left > .layers-control .panel,
.maplibregl-ctrl-bottom-left > .layers-control .panel {
  left: 0;
}

.maplibregl-ctrl-top-right > .layers-control .panel,
.maplibregl-ctrl-bottom-right > .layers-control .panel {
  right: 0;
}

.layers-control.open .panel {
  display: flex;
}

.layers-toggle-btn {
  width: 40px;
  height: 40px;
  background-color: #fff;
  cursor: pointer;
  background-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free v7.0.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--><path d="M296.5 69.2C311.4 62.3 328.6 62.3 343.5 69.2L562.1 170.2C570.6 174.1 576 182.6 576 192C576 201.4 570.6 209.9 562.1 213.8L343.5 314.8C328.6 321.7 311.4 321.7 296.5 314.8L77.9 213.8C69.4 209.8 64 201.3 64 192C64 182.7 69.4 174.1 77.9 170.2L296.5 69.2zM112.1 282.4L276.4 358.3C304.1 371.1 336 371.1 363.7 358.3L528 282.4L562.1 298.2C570.6 302.1 576 310.6 576 320C576 329.4 570.6 337.9 562.1 341.8L343.5 442.8C328.6 449.7 311.4 449.7 296.5 442.8L77.9 341.8C69.4 337.8 64 329.3 64 320C64 310.7 69.4 302.1 77.9 298.2L112 282.4zM77.9 426.2L112 410.4L276.3 486.3C304 499.1 335.9 499.1 363.6 486.3L527.9 410.4L562 426.2C570.5 430.1 575.9 438.6 575.9 448C575.9 457.4 570.5 465.9 562 469.8L343.4 570.8C328.5 577.7 311.3 577.7 296.4 570.8L77.9 469.8C69.4 465.8 64 457.3 64 448C64 438.7 69.4 430.1 77.9 426.2z"/></svg>');
  background-repeat: no-repeat;
  background-position: center;
}

.layers-control .panel::-webkit-scrollbar {
  width: 6px;
}

.layers-control .panel::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
`;

const defaultOptions: LayersControlOptions = {
  title: 'Layers Control',
  customLabels: {},
  legendServiceUrl: undefined,
  opacityControl: false,
  layerGroups: [],
  ungroupedLayers: [],
  ungroupedLayersTitles: [],
  ungroupedLayersVisible: [],
  baseLayersHeading: 'Base layers',
  ungroupedLayersHeading: 'Ungrouped layers',
  onePointZoomLevel: 11.5,
};

class LayersControl implements IControl {
  private map?: Map;
  private container!: HTMLDivElement;
  private panel!: HTMLDivElement;
  private toggleBtn!: HTMLButtonElement;
  private title?: string;
  private customLabels: Record<string, string>;
  private legendServiceUrl?: string;
  private opacityControlOption: boolean;
  private layerGroups: LayerGroupConfig[];
  private ungroupedLayers: string[];
  private ungroupedLayersTitles: string[];
  private ungroupedLayersVisible: boolean[];
  private baseLayersHeading: string;
  private ungroupedLayersHeading: string;
  private onePointZoomLevel: number;
  private groupCheckboxes: Map<string, HTMLInputElement> = new Map();
  private layerBounds: Map<string, maplibregl.LngLatBounds> = new Map();

constructor(options: Partial<LayersControlOptions>) {
  this.title = options.title ?? defaultOptions.title;
  this.customLabels = options.customLabels ?? defaultOptions.customLabels!;
  this.legendServiceUrl = options.legendServiceUrl;
  this.opacityControlOption = options.opacityControl ?? defaultOptions.opacityControl;
  this.layerGroups = options.layerGroups ?? defaultOptions.layerGroups!;
  this.ungroupedLayers = options.ungroupedLayers ?? defaultOptions.ungroupedLayers!;
  this.ungroupedLayersTitles = options.ungroupedLayersTitles ?? defaultOptions.ungroupedLayersTitles!;
  this.ungroupedLayersVisible = options.ungroupedLayersVisible ?? defaultOptions.ungroupedLayersVisible!;
  this.baseLayersHeading = options.baseLayersHeading ?? defaultOptions.baseLayersHeading!;
  this.ungroupedLayersHeading = options.ungroupedLayersHeading ?? defaultOptions.ungroupedLayersHeading!;
  this.onePointZoomLevel = options.onePointZoomLevel ?? defaultOptions.onePointZoomLevel!;
}

  private injectStyles() {
    if (document.getElementById('layers-control-styles')) return;

    const styleEl = document.createElement('style');
    styleEl.id = 'layers-control-styles';
    styleEl.textContent = defaultStyles;
    document.head.appendChild(styleEl);
  }

  private createToggleButton() {
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.className = 'maplibregl-ctrl-icon layers-toggle-btn';
    this.toggleBtn.type = 'button';
    this.toggleBtn.title = this.title!;
    this.toggleBtn.setAttribute('aria-label', 'Mostrar capas');
    this.toggleBtn.addEventListener('click', () => {
    this.container.classList.toggle('open');
    });
    this.container.appendChild(this.toggleBtn);
  }

  private getBaseLayerIds(): string[] {
    return this.map!
      .getStyle()
      .layers?.filter(l => l.metadata?.isbaselayer)
      .map(l => l.id) ?? [];
  }

  private async calculateLayerBounds(layerId: string): Promise<maplibregl.LngLatBounds | null> {
  if (!this.map) return null;
  
  console.log(`Calculating bounds for layer: ${layerId}`);
  
  const layer = this.map.getLayer(layerId);
  if (!layer) {
    console.log(`Layer not found: ${layerId}`);
    return null;
  }
  
  // Get the source of the layer
  const sourceId = layer.source;
  if (!sourceId) {
    console.log(`No source found for layer: ${layerId}`);
    return null;
  }
  
  const source = this.map.getSource(sourceId);
  if (!source) {
    console.log(`Source not found: ${sourceId}`);
    return null;
  }
  
  console.log(`Source type for ${layerId}: ${source.type}`);
  
  // Handle different source types
  if (source.type === 'geojson') {
    // Try to get data from source
    let geojsonData: any = null;
    
    // @ts-ignore - Access internal data if available
    if (source._data) {
      // @ts-ignore
      geojsonData = source._data;
      console.log(`Got data from source._data for ${layerId}`);
      
      // Check if data is nested under a 'geojson' property
      if (geojsonData && geojsonData.geojson) {
        console.log(`Data is nested under 'geojson' property, extracting...`);
        geojsonData = geojsonData.geojson;
      }
      
      console.log('Data type:', geojsonData?.type);
      console.log('Has features:', !!(geojsonData?.features));
      if (geojsonData?.features) {
        console.log('Number of features:', geojsonData.features.length);
      }
    } else if (source.type === 'geojson' && 'data' in source) {
      // @ts-ignore
      geojsonData = source.data;
      console.log(`Got data from source.data for ${layerId}`);
    } else {
      console.log(`No data available for source: ${sourceId}`);
      return null;
    }
    
    // Calculate bounds from GeoJSON
    let bounds = new maplibregl.LngLatBounds();
    let hasFeatures = false;
    
    // Improved coordinate extraction function
    const extractCoordinates = (geom: any) => {
      if (!geom || !geom.coordinates) return;
      
      const coords = geom.coordinates;
      
      // Helper to process a single coordinate pair
      const processCoord = (coord: any) => {
        if (Array.isArray(coord) && coord.length >= 2 && typeof coord[0] === 'number' && typeof coord[1] === 'number') {
          bounds.extend([coord[0], coord[1]]);
          hasFeatures = true;
          return true;
        }
        return false;
      };
      
      // Helper to recursively process nested coordinates
      const processNestedCoords = (coordsArray: any) => {
        if (!Array.isArray(coordsArray)) return;
        
        // Check if this is a coordinate pair
        if (coordsArray.length >= 2 && typeof coordsArray[0] === 'number' && typeof coordsArray[1] === 'number') {
          processCoord(coordsArray);
          return;
        }
        
        // Otherwise, recurse
        for (const item of coordsArray) {
          processNestedCoords(item);
        }
      };
      
      // Handle different geometry types
      switch (geom.type) {
        case 'Point':
          processCoord(coords);
          break;
        case 'LineString':
        case 'MultiPoint':
          processNestedCoords(coords);
          break;
        case 'Polygon':
        case 'MultiLineString':
          // For Polygon: coordinates is an array of rings
          // For MultiLineString: coordinates is an array of line strings
          processNestedCoords(coords);
          break;
        case 'MultiPolygon':
          // For MultiPolygon: coordinates is an array of polygons
          // Each polygon is an array of rings
          for (const polygon of coords) {
            processNestedCoords(polygon);
          }
          break;
        default:
          console.log(`Unknown geometry type: ${geom.type}`);
          // Try to process anyway
          processNestedCoords(coords);
      }
    };
    
    if (geojsonData) {
      if (geojsonData.type === 'FeatureCollection' && Array.isArray(geojsonData.features)) {
        console.log(`Processing FeatureCollection with ${geojsonData.features.length} features`);
        geojsonData.features.forEach((feature: any, index: number) => {
          if (feature.geometry) {
            extractCoordinates(feature.geometry);
          } else {
            console.log(`Feature ${index} has no geometry`);
          }
        });
      } else if (geojsonData.type === 'Feature') {
        console.log(`Processing single Feature`);
        if (geojsonData.geometry) {
          extractCoordinates(geojsonData.geometry);
        }
      } else if (geojsonData.type === 'GeometryCollection' && Array.isArray(geojsonData.geometries)) {
        console.log(`Processing GeometryCollection with ${geojsonData.geometries.length} geometries`);
        geojsonData.geometries.forEach((geometry: any) => {
          extractCoordinates(geometry);
        });
      } else if (geojsonData.coordinates) {
        console.log(`Processing raw geometry`);
        extractCoordinates(geojsonData);
      } else {
        console.log(`Unknown GeoJSON structure:`, geojsonData);
        // Try to see if there's any features property at any level
        if (geojsonData && typeof geojsonData === 'object') {
          for (const key in geojsonData) {
            if (geojsonData[key] && geojsonData[key].type === 'FeatureCollection') {
              console.log(`Found FeatureCollection at property '${key}'`);
              const fc = geojsonData[key];
              if (fc.features) {
                fc.features.forEach((feature: any) => {
                  if (feature.geometry) {
                    extractCoordinates(feature.geometry);
                  }
                });
              }
              break;
            }
          }
        }
      }
    }
    
    console.log(`Bounds calculation for ${layerId} - hasFeatures: ${hasFeatures}`);
    if (hasFeatures) {
      console.log(`Final bounds: SW(${bounds.getSouthWest().lng}, ${bounds.getSouthWest().lat}) NE(${bounds.getNorthEast().lng}, ${bounds.getNorthEast().lat})`);
    }
    return hasFeatures ? bounds : null;
  } else if (source.type === 'vector') {
    // For vector sources, we need to query the source data
    console.log(`Vector source detected for ${layerId}, attempting to query features`);
    
    // Get all visible features from this source
    const features = this.map.querySourceFeatures(sourceId);
    console.log(`Found ${features.length} features in viewport for ${layerId}`);
  
  console.log(`Bounds calculation for ${layerId} - hasFeatures: ${hasFeatures}`);
if (hasFeatures) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const isPoint = sw.lng === ne.lng && sw.lat === ne.lat;
  console.log(`Final bounds: SW(${sw.lng}, ${sw.lat}) NE(${ne.lng}, ${ne.lat}) - isPoint: ${isPoint}`);
}
return hasFeatures ? bounds : null;
    
    if (features.length > 0) {
      let bounds = new maplibregl.LngLatBounds();
      features.forEach(feature => {
        if (feature.geometry) {
          // @ts-ignore
          if (feature.geometry.type === 'Point') {
            // @ts-ignore
            bounds.extend(feature.geometry.coordinates);
          } else if (feature.geometry.type === 'LineString' || feature.geometry.type === 'Polygon') {
            // @ts-ignore
            feature.geometry.coordinates.forEach(coord => {
              if (Array.isArray(coord) && coord.length >= 2) {
                if (typeof coord[0] === 'number') {
                  bounds.extend([coord[0], coord[1]]);
                } else {
                  coord.forEach(c => {
                    if (Array.isArray(c) && c.length >= 2) {
                      bounds.extend([c[0], c[1]]);
                    }
                  });
                }
              }
            });
          }
        }
      });
      return bounds;
    }
    return null;
  }
  
  return null;
}

  // Method to calculate bounds for all layers
  private async calculateAllLayerBounds() {
    if (!this.map) return;
    
    const layers = this.map.getStyle().layers ?? [];
    const regularLayers = layers.filter(l => !l.metadata?.isbaselayer);
    
    // Collect all layer IDs from groups and ungrouped layers
    const allLayerIds = new Set<string>();
    
    // Add layers from groups
    const collectLayerIds = (groups: LayerGroupConfig[]) => {
      groups.forEach(group => {
        if (group.layers) {
          group.layers.forEach(layerId => allLayerIds.add(layerId));
        }
        if (group.subgroups) {
          collectLayerIds(group.subgroups);
        }
      });
    };
    
    collectLayerIds(this.layerGroups);
    
    // Add ungrouped layers
    this.ungroupedLayers.forEach(layerId => allLayerIds.add(layerId));
    
    // Also include any other regular layers if not using custom grouping
    if (this.layerGroups.length === 0 && this.ungroupedLayers.length === 0) {
      regularLayers.forEach(layer => allLayerIds.add(layer.id));
    }
    
    // Calculate bounds for each layer
    const boundsPromises = Array.from(allLayerIds).map(async (layerId) => {
      const bounds = await this.calculateLayerBounds(layerId);
      if (bounds) {
        this.layerBounds.set(layerId, bounds);
      }
    });
    
    await Promise.all(boundsPromises);
  }
  
  // New method to fit map to layer bounds
private fitToLayer(layerId: string) {
  if (!this.map) return;
  
  const bounds = this.layerBounds.get(layerId);
  if (bounds) {
    // Check if bounds are valid (not empty)
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    
    // Check if this is a single point (bounds are the same)
    const isPoint = sw.lng === ne.lng && sw.lat === ne.lat;
    
    if (isPoint) {
      // For point features, center on the point with a specific zoom level
      console.log(`Single point detected for ${layerId}, centering at ${this.onePointZoomLevel} zoom`);
      this.map.flyTo({
        center: [sw.lng, sw.lat],
        zoom: this.onePointZoomLevel,
        duration: 2000
      });
    } else {
      // For polygon/line features, fit to bounds
      console.log(`Fitting bounds for ${layerId}`);
      this.map.fitBounds(bounds, {
        padding: 50,
        duration: 2000
      });
    }
  } else {
    console.log(`No bounds found for layer: ${layerId}`);
    // Try to calculate bounds on the fly as fallback
    this.calculateLayerBounds(layerId).then(bounds => {
      if (bounds) {
        this.layerBounds.set(layerId, bounds);
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const isPoint = sw.lng === ne.lng && sw.lat === ne.lat;
        
        if (isPoint) {
          this.map!.flyTo({
            center: [sw.lng, sw.lat],
            zoom: 11.5,
            duration: 1000
          });
        } else {
          this.map!.fitBounds(bounds, {
            padding: 50,
            duration: 1000
          });
        }
      }
    });
  }
}

// Obtener título personalizado para una capa según su índice en el array
private getCustomTitle(layerId: string, index: number, customTitles?: string[]): string {
  // Verificar si customTitles existe y tiene un valor válido en ese índice
  if (customTitles && Array.isArray(customTitles) && customTitles.length > index) {
    const title = customTitles[index];
    if (title && typeof title === 'string' && title.trim() !== '') {
      return title;
    }
  }
  // Si no hay título personalizado, usar customLabels o el layerId
  return this.customLabels[layerId] ?? layerId;
}

private renderGroup(group: LayerGroupConfig, layers: any[], assignedLayers: Set<string>, level: number = 0): HTMLElement {
  const groupElement = document.createElement('div');
  groupElement.className = 'tree-group';
  
  const childrenContainer = document.createElement('div');
  childrenContainer.className = `group-children ${group.collapsed ? 'collapsed' : ''}`;
  
  if ((group.layers && group.layers.length > 0) || (group.subgroups && group.subgroups.length > 0)) {
    const groupCheckbox = this.createGroupHeader(group, groupElement, childrenContainer, level);
    
    if (group.layers) {
      group.layers.forEach((layerId, idx) => {
        const layer = layers.find(l => l.id === layerId);
        if (layer && !layer.metadata?.isbaselayer) {
          const customTitle = this.getCustomTitle(layerId, idx, group.layersTitles);
          let initialVisible = true;
          if (group.layersVisible && group.layersVisible.length > idx) {
            initialVisible = group.layersVisible[idx] === true;
          }
          const layerItem = this.createLayerItem(layer.id, layer.type, false, customTitle, initialVisible);
          childrenContainer.appendChild(layerItem);
          assignedLayers.add(layerId);
        } else if (layer && layer.metadata?.isbaselayer) {
          const customTitle = this.getCustomTitle(layerId, idx, group.layersTitles);
          let initialVisible = true;
          if (group.layersVisible && group.layersVisible.length > idx) {
            initialVisible = group.layersVisible[idx] === true;
          }
          const layerItem = this.createLayerItem(layer.id, layer.type, true, customTitle, initialVisible);
          childrenContainer.appendChild(layerItem);
          assignedLayers.add(layerId);
        }
      });
    }
    
    if (group.subgroups) {
      group.subgroups.forEach(subGroup => {
        const subGroupElement = this.renderGroup(subGroup, layers, assignedLayers, level + 1);
        childrenContainer.appendChild(subGroupElement);
      });
    }
    
    groupElement.appendChild(childrenContainer);
    this.updateGroupCheckboxState(group, groupCheckbox);
    
    if (group.layers) {
      group.layers.forEach(layerId => {
        const layerItem = document.getElementById(layerId) as HTMLInputElement;
        if (layerItem) {
          layerItem.addEventListener('change', () => {            
            this.updateAllGroupStates();
          });
        }
      });
    }
  }
  
  return groupElement;
}

// Obtener estado de visibilidad inicial para una capa según su índice
private getInitialVisibility(index: number, visibleArray?: boolean[]): boolean {
  // Si no hay array de visibilidad, retornar true (visible)
  if (!visibleArray || !Array.isArray(visibleArray)) {
    return true;
  }
  
  // Si el índice está fuera del array, retornar true (visible) en lugar de false
  if (index >= visibleArray.length) {
    return true;  // Cambiado de false a true
  }
  
  // Obtener el valor en el índice
  const visible = visibleArray[index];
  
  // Si el valor es undefined o null, retornar true (visible)
  if (visible === undefined || visible === null) {
    return true;
  }
  
  // Retornar el valor booleano
  return visible === true;
}

// Check/uncheck all layers in a group (including subgroups)
private setGroupVisibility(group: LayerGroupConfig, visible: boolean) {
  // Handle direct layers
  if (group.layers) {
    group.layers.forEach(layerId => {
      const layer = this.map!.getLayer(layerId);
      if (layer) {
        const isBaseLayer = layer.metadata?.isbaselayer;
        if (isBaseLayer) {
          if (visible) {
            this.map!.setLayoutProperty(layerId, 'visibility', 'visible');
          }
        } else {
          this.map!.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }
        
        const checkbox = document.getElementById(layerId) as HTMLInputElement;
        if (checkbox && !isBaseLayer) {
          checkbox.checked = visible;
        }
      }
    });
  }
  
  // Recursively handle subgroups
  if (group.subgroups) {
    group.subgroups.forEach(subGroup => {
      this.setGroupVisibility(subGroup, visible);
    });
  }
}

// Update all group checkboxes in the entire tree
private updateAllGroupStates() {
  const updateGroupRecursively = (g: LayerGroupConfig) => {
    const groupCheckbox = this.groupCheckboxes.get(g.id); // Usar el Map en lugar de getElementById
    if (groupCheckbox) {
      this.updateGroupCheckboxState(g, groupCheckbox);
    }
    if (g.subgroups) {
      g.subgroups.forEach(subGroup => updateGroupRecursively(subGroup));
    }
  };
  
  this.layerGroups.forEach(group => updateGroupRecursively(group));
}

// Update group checkbox state based on children (including subgroups)
private updateGroupCheckboxState(group: LayerGroupConfig, groupCheckbox: HTMLInputElement) {
  let foundVisible = false;
  let foundHidden = false;
  
  // Función recursiva que retorna true cuando encuentra estado mixto
  const scanLayers = (g: LayerGroupConfig): boolean => {
    // Verificar capas directas
    if (g.layers) {
      for (const layerId of g.layers) {
        const layer = this.map!.getLayer(layerId);
        if (layer && !layer.metadata?.isbaselayer) {
          const visibility = this.map!.getLayoutProperty(layerId, 'visibility');
          const isVisible = visibility === 'visible';
          
          if (isVisible) {
            foundVisible = true;
          } else {
            foundHidden = true;
          }
          
          // Si ya tenemos ambos estados, podemos detener el escaneo
          if (foundVisible && foundHidden) {
            return true;
          }
        }
      }
    }
    
    // Verificar subgrupos recursivamente
    if (g.subgroups && !(foundVisible && foundHidden)) {
      for (const subGroup of g.subgroups) {
        if (scanLayers(subGroup)) {
          return true;
        }
      }
    }
    
    return false;
  };
  
  scanLayers(group);
  
  // Asignar estado al checkbox
  if (!foundVisible && !foundHidden) {
    // Sin capas regulares en este grupo
    groupCheckbox.checked = false;
    groupCheckbox.indeterminate = false;
  } else if (foundVisible && !foundHidden) {
    groupCheckbox.checked = true;
    groupCheckbox.indeterminate = false;
  } else if (!foundVisible && foundHidden) {
    groupCheckbox.checked = false;
    groupCheckbox.indeterminate = false;
  } else {
    groupCheckbox.checked = false;
    groupCheckbox.indeterminate = true;
  }
}

// Create a group header with checkbox and toggle
private createGroupHeader(group: LayerGroupConfig, groupElement: HTMLElement, childrenContainer: HTMLElement, level: number = 0): HTMLInputElement {
  const header = document.createElement('div');
  header.className = 'group-header';
  
  const toggleBtn = document.createElement('span');
  toggleBtn.className = 'group-toggle';
  toggleBtn.textContent = group.collapsed ? '\u25B6' : '\u25BC';
  toggleBtn.style.cursor = 'pointer';
  
  const groupCheckbox = document.createElement('input');
  groupCheckbox.type = 'checkbox';
  groupCheckbox.className = 'group-checkbox';
  groupCheckbox.id = `${group.id}-checkbox`;
  this.groupCheckboxes.set(group.id, groupCheckbox);
  groupCheckbox.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    this.setGroupVisibility(group, checked);
    groupCheckbox.indeterminate = false;
    this.updateAllGroupStates();
  });
  
  const nameSpan = document.createElement('span');
  nameSpan.className = 'group-name';
  nameSpan.textContent = group.heading;  // Usar 'heading' en lugar de 'name'
  
  header.appendChild(toggleBtn);
  header.appendChild(groupCheckbox);
  header.appendChild(nameSpan);
  
  // Función para toggle expand/collapse
  const toggleExpandCollapse = () => {
    if (childrenContainer) {
      const isCollapsed = childrenContainer.classList.contains('collapsed');
      if (isCollapsed) {
        childrenContainer.classList.remove('collapsed');
        toggleBtn.textContent = '\u25BC';
      } else {
        childrenContainer.classList.add('collapsed');
        toggleBtn.textContent = '\u25B6';
      }
    }
  };
  
  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleExpandCollapse();
  });
  
  header.addEventListener('click', (e) => {
    if ((e.target as HTMLElement) !== groupCheckbox) {
      toggleExpandCollapse();
    }
  });
  
  groupElement.appendChild(header);
  return groupCheckbox;
}

private buildPanel() {
  this.panel.innerHTML = `<h2 class='panel-title'>${this.title}</h2>`;

  const layers = this.map!.getStyle().layers ?? [];
  const baseLayers = layers.filter(l => l.metadata?.isbaselayer);
  const regularLayers = layers.filter(l => !l.metadata?.isbaselayer);

  // Build base layers section
  if (baseLayers.length > 0) {
    const baseGroup = document.createElement('div');
    baseGroup.className = 'tree-group';
    const baseHeader = document.createElement('div');
    baseHeader.className = 'group-header';
    baseHeader.style.fontWeight = 'bold';
    baseHeader.innerHTML = `<span style="margin-right: 8px;">\u{1F5FA}</span><span>${this.baseLayersHeading}</span>`;
    baseGroup.appendChild(baseHeader);

    const baseChildren = document.createElement('div');
    baseChildren.className = 'group-children';

    baseLayers.forEach(layer => {
      const layerItem = this.createLayerItem(layer.id, layer.type, true);
      baseChildren.appendChild(layerItem);
    });

    baseGroup.appendChild(baseChildren);
    this.panel.appendChild(baseGroup);
  }

  const assignedLayers = new Set<string>();
  const hasGroups = this.layerGroups && this.layerGroups.length > 0;
  const hasUngroupedLayers = this.ungroupedLayers && this.ungroupedLayers.length > 0;

  if (hasGroups || hasUngroupedLayers) {
    // Renderizar grupos
    if (hasGroups) {
      this.layerGroups.forEach(group => {
        const groupElement = this.renderGroup(group, layers, assignedLayers, 0);
        this.panel.appendChild(groupElement);
      });
    }
    
    // Renderizar capas sueltas (ungrouped)
    if (hasUngroupedLayers) {
      const ungroupedContainer = document.createElement('div');
      ungroupedContainer.className = 'tree-group';
      
      const ungroupedHeader = document.createElement('div');
      ungroupedHeader.className = 'group-header';
      ungroupedHeader.innerHTML = `<span style="margin-right: 8px;">\u{1F4C1}</span><span>${this.ungroupedLayersHeading}</span>`;
      ungroupedContainer.appendChild(ungroupedHeader);
      
      const ungroupedChildren = document.createElement('div');
      ungroupedChildren.className = 'group-children';
      ungroupedChildren.style.marginLeft = '0';
      ungroupedChildren.style.paddingLeft = '0';
      ungroupedChildren.style.borderLeft = 'none';
      
      this.ungroupedLayers.forEach((layerId, idx) => {
        const layer = layers.find(l => l.id === layerId);
        if (layer && !layer.metadata?.isbaselayer) {
          const customTitle = this.getCustomTitle(layerId, idx, this.ungroupedLayersTitles);
          let initialVisible = true;
          if (this.ungroupedLayersVisible && this.ungroupedLayersVisible.length > idx) {
            initialVisible = this.ungroupedLayersVisible[idx] === true;
          }
          const layerItem = this.createLayerItem(layer.id, layer.type, false, customTitle, initialVisible);
          ungroupedChildren.appendChild(layerItem);
          assignedLayers.add(layerId);
        }
      });
      
      ungroupedContainer.appendChild(ungroupedChildren);
      this.panel.appendChild(ungroupedContainer);
    }
  } else {
    // Modo por defecto: mostrar TODAS las capas regulares
    const allRegularLayers = regularLayers;
    if (allRegularLayers.length > 0) {
      allRegularLayers.forEach(layer => {
        const layerItem = this.createLayerItem(layer.id, layer.type, false);
        layerItem.style.marginLeft = '0';
        layerItem.style.paddingLeft = '0';
        this.panel.appendChild(layerItem);
      });
    }
  }
}

  private createLayerItem(layerId: string, type: string, isBaseLayer: boolean, customTitle?: string, initialVisible: boolean = true): HTMLElement {
  const layerDiv = document.createElement('div');
  layerDiv.className = 'layer-item';
  
  // Usar título personalizado si existe, sino el de customLabels, sino el layerId
  const labelTitle = customTitle ?? this.customLabels[layerId] ?? layerId;
  
  if (isBaseLayer) {
    const baseIds = this.getBaseLayerIds();
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'base-layer';
    input.id = layerId;
    
    const initId = baseIds[0];
    if (layerId === initId) {
      input.checked = true;
      this.map!.setLayoutProperty(layerId, 'visibility', 'visible');
    } else {
      input.checked = false;
      this.map!.setLayoutProperty(layerId, 'visibility', 'none');
    }
    
    input.addEventListener('change', () => {
      baseIds.forEach(id => {
        const checked = (id === layerId);
        this.map!.setLayoutProperty(id, 'visibility', checked ? 'visible' : 'none');
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (el) el.checked = checked;
      });
      this.updateAllGroupStates();
    });
    
    const label = document.createElement('label');
    label.htmlFor = layerId;
    label.textContent = labelTitle;
    
    layerDiv.appendChild(input);
    layerDiv.appendChild(label);
  } else {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = layerId;
    
    // Forzar el estado según initialVisible
    input.checked = initialVisible;
    this.map!.setLayoutProperty(layerId, 'visibility', initialVisible ? 'visible' : 'none');
    
    input.addEventListener('change', (e) => {
      const chk = (e.target as HTMLInputElement).checked;
      this.map!.setLayoutProperty(layerId, 'visibility', chk ? 'visible' : 'none');
      this.updateAllGroupStates();
    });
    
    const label = document.createElement('label');
    // IMPORTANT: Remove the 'for' attribute to prevent default toggle behavior
    // label.htmlFor = layerId;  // COMMENT THIS OUT
    label.textContent = labelTitle;
    
    // Make label clickable for centering
    label.style.cursor = 'pointer';
    
    // Add click handler to label
label.addEventListener('click', async (e) => {
  e.preventDefault();
  e.stopPropagation();
  
  // Only center if the layer is visible
  if (input.checked) {
    // Add visual feedback
    const originalText = label.textContent;
    label.textContent = '📍 ' + originalText;
    label.style.opacity = '0.7';
    
    this.fitToLayer(layerId);
    
    // Restore after animation
    setTimeout(() => {
      label.textContent = originalText;
      updateLabelStyle();
    }, 1000);
  }
});
    
    // Change cursor style based on visibility
    const updateLabelStyle = () => {
      if (input.checked) {
        label.style.cursor = 'pointer';
        label.style.opacity = '1';
      } else {
        label.style.cursor = 'default';
        label.style.opacity = '0.75';
        label.style.textDecoration = 'none';
      }
    };
    
    // Initial style update
    updateLabelStyle();
    
    // Update style when visibility changes
    input.addEventListener('change', updateLabelStyle);
    
    layerDiv.appendChild(input);
    layerDiv.appendChild(label);
  }
  
  if (this.opacityControlOption && type !== 'symbol') {
    const opacityWrapper = document.createElement('div');
    opacityWrapper.className = 'opacity-row';
    
    const range = document.createElement('input');
    range.type = 'range';
    range.min = '0';
    range.max = '100';
    
    let propKey: string = `${type}-opacity`;
    const curr = Number(this.map!.getPaintProperty(layerId, propKey));
    range.value = isNaN(curr) ? '100' : String(Math.round(curr * 100));
    
    range.addEventListener('input', (e) => {
      const v = Number((e.target as HTMLInputElement).value) / 100;
      this.map!.setPaintProperty(layerId, propKey, v);
    });
    
    opacityWrapper.appendChild(range);
    layerDiv.appendChild(opacityWrapper);
  }
  
  if (this.legendServiceUrl && type !== 'symbol') {
    this.fetchAndRenderLegend(layerId, layerDiv);
  }
  
  return layerDiv;
}

  private async fetchAndRenderLegend(layerId: string, parent: HTMLElement) {
    const url = new URL(this.legendServiceUrl!);
    url.searchParams.set('layer_id', layerId);
    url.searchParams.set('has_label', 'false');
    url.searchParams.set('include_raster', 'true');

    const legend = document.createElement('div');
    legend.className = 'legend-svg';
    legend.textContent = 'Cargando�';
    parent.appendChild(legend);

    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(String(res.status));
      const svg = await res.text();
      legend.innerHTML = svg;
    } catch (err) {
      legend.textContent = `Error leyenda: ${err}`;
      legend.style.color = 'red';
    }
  }

 onAdd(map: Map): HTMLElement {
  this.map = map;

  this.injectStyles();

  this.container = document.createElement('div');
  this.container.className = 'maplibregl-ctrl layers-control';

  this.createToggleButton();

  this.panel = document.createElement('div');
  this.panel.className = 'panel';
  this.container.appendChild(this.panel);

  map.on('load', async () => {
    this.buildPanel();
    
    // Wait for all sources to be loaded
    const sources = map.getStyle().sources;
    const sourcePromises = Object.keys(sources).map(sourceId => {
      return new Promise((resolve) => {
        const source = map.getSource(sourceId);
        if (source && source.type === 'geojson') {
          // @ts-ignore
          if (source._data) {
            resolve(true);
          } else {
            // Wait for source to load
            map.on('sourcedata', function onSourceData(e) {
              if (e.sourceId === sourceId && e.isSourceLoaded) {
                map.off('sourcedata', onSourceData);
                resolve(true);
              }
            });
          }
        } else {
          resolve(true);
        }
      });
    });
    
    await Promise.all(sourcePromises);
    
    // Calculate bounds after all sources are loaded
    console.log('All sources loaded, calculating bounds...');
    await this.calculateAllLayerBounds();
    console.log('Bounds calculated:', Array.from(this.layerBounds.keys()));
  });

  return this.container;
}

  onRemove() {
    this.container.parentNode?.removeChild(this.container);
    this.map = undefined;
  }

  getDefaultPosition(): ControlPosition {
    return 'top-left';
  }
}

(function(global: any) {
  global.LayersControl = LayersControl;
})(window);

export default LayersControl;